import React, { useState, useEffect } from "react";
import {
  FaDollarSign, FaCalendarAlt, FaClock, FaMapMarkerAlt,
  FaCheckCircle, FaUsers, FaCreditCard, FaTimes, FaStickyNote,
  FaFileAlt, FaArrowRight, FaShieldAlt, FaWallet, FaChevronRight,
} from "react-icons/fa";
import stateCityData from "../../../data/states.json";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const PaidPressConference = () => {
  const [formData, setFormData] = useState({
    topic: "", purpose: "", conferenceDate: "", conferenceTime: "",
    timePeriod: "AM", state: "", city: "", place: "", landmark: "",
    adminNote: "", noReporterNeeded: false, numberOfReporters: "",
    termsAccepted: false,
  });

  const [states] = useState(Object.keys(stateCityData));
  const [cities, setCities] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [alertProgress, setAlertProgress] = useState(100);
  const [showDetails, setShowDetails] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [pricingData, setPricingData] = useState(null);
  const [showPricing, setShowPricing] = useState(false);
  const [isCalculatingPrice, setIsCalculatingPrice] = useState(false);
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);
  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false);
  const [pendingPaymentMethod, setPendingPaymentMethod] = useState("");
  const [paymentConfirmationChecked, setPaymentConfirmationChecked] = useState(false);

  const wordCount = formData.purpose.trim().split(/\s+/).filter((w) => w.length > 0).length;

  // Auto-dismiss error alert
  useEffect(() => {
    if (!showErrorAlert) return;
    setAlertProgress(100);
    const interval = setInterval(() => {
      setAlertProgress((p) => { if (p <= 0) { setShowErrorAlert(false); return 100; } return p - 2; });
    }, 100);
    const timer = setTimeout(() => { setShowErrorAlert(false); setAlertProgress(100); }, 5000);
    return () => { clearInterval(interval); clearTimeout(timer); };
  }, [showErrorAlert]);

  const fetchWalletBalance = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/press/wallet/detail`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("pressToken")}` },
      });
      setWalletBalance(res.data.data?.balance || res.data.balance || 0);
    } catch { setWalletBalance(0); }
  };

  useEffect(() => {
    if (formData.numberOfReporters >= 1) calculatePricingForReporters(formData.numberOfReporters);
    fetchWalletBalance();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let updated = { ...formData, [name]: type === "checkbox" ? checked : value };
    if (name === "conferenceTime" && value) {
      updated.timePeriod = parseInt(value.split(":")[0], 10) >= 12 ? "PM" : "AM";
    }
    if (name === "numberOfReporters") {
      let numValue = value === "" ? "" : parseInt(value, 10);
      if (value !== "" && (isNaN(numValue) || numValue < 0)) return;
      if (numValue > 50) numValue = 50;
      updated = { ...updated, numberOfReporters: numValue, noReporterNeeded: numValue === 0 || numValue === "" };
      if (numValue >= 1) calculatePricingForReporters(numValue);
      else { setShowPricing(false); setPricingData(null); }
    }
    setFormData(updated);
    setFormError("");
    setShowErrorAlert(false);
  };

  const calculatePricingForReporters = async (count) => {
    if (!count || count < 1) { setPricingData(null); setShowPricing(false); return; }
    setIsCalculatingPrice(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/press/paid-conference/calculate-price`,
        { numberOfReporters: count },
        { headers: { Authorization: `Bearer ${localStorage.getItem("pressToken")}`, "Content-Type": "application/json" } }
      );
      if (res.data.success) { setPricingData(res.data.data); setShowPricing(true); }
      else { setErrorMessage(res.data.message || "Failed to calculate pricing"); setShowErrorAlert(true); }
    } catch { setErrorMessage("Failed to calculate pricing. Try again."); setShowErrorAlert(true); }
    finally { setIsCalculatingPrice(false); }
  };

  const handleStateChange = (e) => {
    const s = e.target.value;
    setFormData({ ...formData, state: s, city: "" });
    setCities(s ? (stateCityData[s] || []) : []);
  };

  const showErr = (msg) => { setErrorMessage(msg); setShowErrorAlert(true); };

  const validateForm = () => {
    const req = [
      { key: "topic", label: "Topic" }, { key: "purpose", label: "Purpose" },
      { key: "conferenceDate", label: "Date" }, { key: "conferenceTime", label: "Time" },
      { key: "state", label: "State" }, { key: "city", label: "City" },
      { key: "place", label: "Venue" }, { key: "landmark", label: "Address" },
    ];
    const missing = req.filter((f) => !formData[f.key]);
    if (missing.length) { showErr(`Missing: ${missing.map((f) => f.label).join(", ")}`); return false; }
    const rc = formData.numberOfReporters;
    if (rc === "" || rc === null || rc < 1) { showErr("Please enter at least 1 reporter"); return false; }
    if (rc > 50) { showErr("Maximum 50 reporters allowed"); return false; }
    if (!formData.termsAccepted) { showErr("Please accept the terms and conditions"); return false; }
    if (wordCount < 10) { showErr("Purpose must be at least 10 words"); return false; }
    return true;
  };

  const processPayment = async () => {
    if (!pricingData || pricingData.totalAmount === 0) { showErr("Payment calculation error. Please try again."); setIsProcessingPayment(false); return; }
    setIsProcessingPayment(true);
    try {
      const token = localStorage.getItem("pressToken");
      const orderResponse = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/press/paid-conference/create-order`,
        { numberOfReporters: formData.numberOfReporters },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      if (!orderResponse.data.success) { showErr(orderResponse.data.message || "Failed to create payment order"); setIsProcessingPayment(false); return; }
      const orderData = orderResponse.data.data;
      setPaymentOrder(orderData);
      const options = {
        key: orderResponse.data.data.key || process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderData.amount, currency: orderData.currency, order_id: orderData.order_id,
        name: "IINSAF Press Conference",
        description: `Paid Press Conference - ${formData.numberOfReporters} Reporter(s)`,
        prefill: { name: "Press Conference User", email: "user@example.com" },
        theme: { color: "#F59E0B" },
        handler: async (response) => {
          try {
            const verifyRes = await axios.get(
              `${process.env.REACT_APP_API_BASE_URL}/press/paid-conference/payment/${response.razorpay_payment_id}`,
              { headers: { Authorization: `Bearer ${localStorage.getItem("pressToken")}` } }
            );
            if (verifyRes.data.status === "captured") await submitConference(response.razorpay_payment_id, orderData.order_id);
            else toast.error("Payment verification failed");
          } catch { toast.error("Payment verification failed"); }
        },
      };
      if (window.Razorpay) { const rp = new window.Razorpay(options); rp.open(); }
      else toast.error("Payment gateway not loaded. Please refresh.");
    } catch { showErr("Payment processing failed. Please try again."); }
    finally { setIsProcessingPayment(false); }
  };

  const submitConference = async (paymentId = null, orderId = null) => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/press/paid-conference/submit`,
        { ...formData, paymentId, orderId },
        { headers: { Authorization: `Bearer ${localStorage.getItem("pressToken")}`, "Content-Type": "application/json" } }
      );
      if (res.data.success) {
        toast.success("Paid conference submitted successfully!");
        setSubmittedData({ ...formData, conferenceId: res.data.data.conferenceId, paymentStatus: res.data.data.paymentStatus, totalAmount: pricingData?.totalAmount || 0 });
        setShowDetails(true);
        setTimeout(() => { window.location.href = "/press/paid-conference-status"; }, 2500);
      } else { showErr(res.data.message || "Failed to submit conference"); }
    } catch { showErr("Failed to submit conference. Please try again."); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError("");
    if (!validateForm()) { setIsSubmitting(false); return; }
    setShowPaymentOptions(true);
    setIsSubmitting(false);
  };

  const handleWalletPaymentClick = () => {
    if (!pricingData) { toast.error("Pricing data not available"); return; }
    if (walletBalance < pricingData.totalAmount) { toast.error("Insufficient wallet balance"); return; }
    setPendingPaymentMethod("wallet"); setShowPaymentConfirmation(true);
  };

  const handleWalletPayment = async () => {
    setIsProcessingPayment(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/press/paid-conference/submit`,
        { ...formData, paymentMethod: "wallet", paymentAmount: pricingData.totalAmount, paymentStatus: "paid" },
        { headers: { Authorization: `Bearer ${localStorage.getItem("pressToken")}` } }
      );
      if (res.data.success) {
        toast.success("Conference submitted with wallet payment!");
        setShowPaymentOptions(false); setShowDetails(true); setSubmittedData(res.data.data);
        await fetchWalletBalance();
        setTimeout(() => { window.location.href = "/press/paid-conference-status"; }, 2500);
      } else { toast.error(res.data.message || "Failed to submit"); }
    } catch { toast.error("Failed to submit with wallet payment"); }
    finally { setIsProcessingPayment(false); }
  };

  const handleRazorpayPaymentClick = () => { setPendingPaymentMethod("razorpay"); setShowPaymentConfirmation(true); };

  const handlePaymentConfirmation = async () => {
    if (!paymentConfirmationChecked) { toast.error("Please confirm the payment first"); return; }
    setShowPaymentConfirmation(false); setShowPaymentOptions(false); setPaymentConfirmationChecked(false);
    if (pendingPaymentMethod === "wallet") await handleWalletPayment();
    else await processPayment();
  };

  const handleReset = () => {
    setFormData({ topic: "", purpose: "", conferenceDate: "", conferenceTime: "", timePeriod: "AM", state: "", city: "", place: "", landmark: "", adminNote: "", noReporterNeeded: false, numberOfReporters: "", termsAccepted: false });
    setCities([]); setShowDetails(false); setSubmittedData(null); setFormError(""); setShowErrorAlert(false); setPricingData(null); setShowPricing(false); setPaymentOrder(null);
  };

  const inputCls = (required, val) =>
    `w-full px-3 py-2.5 text-sm bg-white border ${required && !val ? "border-red-200 focus:ring-red-400" : "border-slate-200 focus:ring-amber-400"} rounded-xl shadow-sm focus:ring-2 focus:border-transparent outline-none transition-all`;

  // ── Success View ──
  if (showDetails && submittedData) {
    return (
      <div className="min-h-screen bg-[#f4f6fb] p-3 sm:p-6 pb-8">
        <ToastContainer />
        <div className="relative bg-gradient-to-br from-amber-500 to-yellow-600 rounded-[1.5rem] p-5 sm:p-8 mb-4 overflow-hidden">
          <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-white/5" />
          <div className="relative z-10 flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-white/20 border border-white/30 flex items-center justify-center flex-shrink-0">
              <FaCheckCircle className="text-white w-6 h-6" />
            </div>
            <div>
              <p className="text-white/60 text-[10px] font-bold uppercase tracking-widest">Submitted</p>
              <h1 className="text-lg sm:text-xl font-black text-white">Paid Conference Submitted!</h1>
              <p className="text-white/70 text-xs mt-0.5">Redirecting to status in a moment...</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="px-4 py-3 bg-gradient-to-r from-amber-600 to-yellow-700 flex items-center gap-2">
            <FaFileAlt className="text-white/70 w-3.5 h-3.5" />
            <span className="text-white text-xs font-black uppercase tracking-widest">Conference Details</span>
          </div>
          <div className="p-4 space-y-3">
            {[
              { label: "Topic", val: submittedData.topic },
              { label: "Date & Time", val: `${submittedData.conferenceDate} at ${submittedData.conferenceTime} ${submittedData.timePeriod}` },
              { label: "Location", val: `${submittedData.city}, ${submittedData.state}` },
              { label: "Venue", val: submittedData.place },
              { label: "Reporters", val: submittedData.numberOfReporters },
              { label: "Conference ID", val: submittedData.conferenceId },
            ].map((r, i) => (
              <div key={i} className="flex justify-between items-start py-2 border-b border-slate-50 last:border-0">
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">{r.label}</span>
                <span className="text-xs font-black text-slate-800 text-right max-w-[55%]">{r.val}</span>
              </div>
            ))}
            {submittedData.totalAmount > 0 && (
              <div className="flex justify-between items-center py-2.5 px-3 bg-amber-50 rounded-xl border border-amber-100">
                <span className="text-xs font-bold text-amber-700">Total Paid</span>
                <span className="text-sm font-black text-amber-700">₹{submittedData.totalAmount}</span>
              </div>
            )}
          </div>
          <div className="p-4 flex gap-2">
            <button onClick={handleReset} className="flex-1 py-3 bg-slate-100 text-slate-700 rounded-xl text-xs font-black active:scale-95 transition-all">
              New Conference
            </button>
            <button onClick={() => window.location.href = "/press/paid-conference-status"} className="flex-1 py-3 bg-gradient-to-r from-amber-500 to-yellow-600 text-white rounded-xl text-xs font-black shadow-lg shadow-amber-100 active:scale-95 transition-all flex items-center justify-center gap-1.5">
              View Status <FaChevronRight className="w-2.5 h-2.5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f6fb] p-3 sm:p-6 pb-8">
      <ToastContainer position="top-right" autoClose={4000} />

      {/* ── Error Toast ── */}
      {showErrorAlert && (
        <div className="fixed top-3 right-3 left-3 sm:left-auto sm:right-4 sm:top-4 z-50 sm:max-w-sm">
          <div className="bg-rose-600 text-white px-4 py-3 rounded-2xl shadow-2xl relative overflow-hidden flex items-start gap-3">
            <div className="absolute bottom-0 left-0 h-0.5 bg-rose-300 transition-all duration-100" style={{ width: `${alertProgress}%` }} />
            <FaTimes className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p className="text-sm font-semibold flex-1 leading-snug">{errorMessage}</p>
            <button onClick={() => setShowErrorAlert(false)}><FaTimes className="w-3.5 h-3.5 text-rose-200 hover:text-white" /></button>
          </div>
        </div>
      )}

      {/* ── Hero Header ── */}
      <div className="relative bg-gradient-to-br from-amber-500 via-yellow-500 to-orange-500 rounded-[1.5rem] p-4 sm:p-8 mb-4 overflow-hidden">
        <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-white/5" />
        <div className="absolute bottom-0 left-1/3 w-40 h-40 rounded-full bg-white/5 translate-y-1/2" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-white/20 backdrop-blur-md border border-white/30 flex items-center justify-center flex-shrink-0">
            <FaDollarSign className="text-white w-5 h-5 sm:w-6 sm:h-6" />
          </div>
          <div>
            <p className="text-white/60 text-[10px] font-bold uppercase tracking-widest">Premium</p>
            <h1 className="text-lg sm:text-2xl font-black text-white tracking-tight leading-tight">Paid Press Conference</h1>
            <p className="text-white/70 text-xs font-medium mt-0.5 hidden sm:block">Priority processing · Premium features</p>
          </div>
        </div>
      </div>

      {/* ── Guidelines Banner ── */}
      <div className="bg-amber-50 border border-amber-100 rounded-2xl p-3 sm:p-4 mb-4">
        <p className="text-amber-700 text-[11px] font-black uppercase tracking-widest mb-2">Guidelines</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
          {["Priority processing & faster approval", "Payment required per reporter booked", "Conference confirmed after payment verification", "Enhanced support throughout your event"].map((g, i) => (
            <div key={i} className="flex items-center gap-1.5 text-amber-700 text-xs font-medium">
              <FaCheckCircle className="w-3 h-3 text-amber-400 flex-shrink-0" /> {g}
            </div>
          ))}
        </div>
      </div>

      {/* ── Main Form ── */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <form onSubmit={handleSubmit}>

          {/* Basic Info */}
          <div className="px-4 py-3 bg-gradient-to-r from-slate-700 to-slate-800 flex items-center gap-2">
            <FaFileAlt className="text-white/70 w-3.5 h-3.5" />
            <span className="text-white text-xs font-black uppercase tracking-widest">Basic Information</span>
          </div>
          <div className="p-4 sm:p-6 space-y-4">
            <div>
              <label className="block text-xs font-black text-slate-600 uppercase tracking-wider mb-1.5">
                Topic of Conference <span className="text-rose-400">*</span>
              </label>
              <input type="text" name="topic" value={formData.topic} onChange={handleChange}
                className={inputCls(true, formData.topic)} placeholder="Enter the conference topic" />
            </div>
            <div>
              <label className="block text-xs font-black text-slate-600 uppercase tracking-wider mb-1.5 flex items-center justify-between">
                <span>Purpose / Press Note <span className="text-rose-400">*</span></span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${wordCount >= 10 ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
                  {wordCount}/10 words
                </span>
              </label>
              <textarea name="purpose" value={formData.purpose} onChange={handleChange} rows={4}
                className={`${inputCls(true, formData.purpose)} resize-none`}
                placeholder="Describe the purpose of your press conference (min 10 words)" />
            </div>
          </div>

          {/* Schedule */}
          <div className="px-4 py-3 bg-gradient-to-r from-blue-700 to-indigo-800 flex items-center gap-2">
            <FaCalendarAlt className="text-white/70 w-3.5 h-3.5" />
            <span className="text-white text-xs font-black uppercase tracking-widest">Schedule</span>
          </div>
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-black text-slate-600 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                  <FaCalendarAlt className="w-3 h-3 text-blue-500" /> Date <span className="text-rose-400">*</span>
                </label>
                <input type="date" name="conferenceDate" value={formData.conferenceDate} onChange={handleChange}
                  min={new Date().toISOString().split("T")[0]} className={inputCls(true, formData.conferenceDate)} />
              </div>
              <div>
                <label className="block text-xs font-black text-slate-600 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                  <FaClock className="w-3 h-3 text-blue-500" /> Time <span className="text-rose-400">*</span>
                </label>
                <input type="time" name="conferenceTime" value={formData.conferenceTime} onChange={handleChange}
                  className={inputCls(true, formData.conferenceTime)} />
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="px-4 py-3 bg-gradient-to-r from-emerald-600 to-teal-700 flex items-center gap-2">
            <FaMapMarkerAlt className="text-white/70 w-3.5 h-3.5" />
            <span className="text-white text-xs font-black uppercase tracking-widest">Location</span>
          </div>
          <div className="p-4 sm:p-6 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-black text-slate-600 uppercase tracking-wider mb-1.5">
                  State <span className="text-rose-400">*</span>
                </label>
                <select name="state" value={formData.state} onChange={handleStateChange} className={inputCls(true, formData.state)}>
                  <option value="">Select State</option>
                  {states.map((s, i) => <option key={i} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-black text-slate-600 uppercase tracking-wider mb-1.5">
                  City <span className="text-rose-400">*</span>
                </label>
                <select name="city" value={formData.city} onChange={handleChange}
                  className={`${inputCls(true, formData.city)} ${!formData.state ? "opacity-50 cursor-not-allowed" : ""}`}
                  disabled={!formData.state}>
                  <option value="">Select City</option>
                  {cities.map((c, i) => <option key={i} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-black text-slate-600 uppercase tracking-wider mb-1.5">
                Venue / Place Name <span className="text-rose-400">*</span>
              </label>
              <input type="text" name="place" value={formData.place} onChange={handleChange}
                className={inputCls(true, formData.place)} placeholder="Enter the full venue name" />
            </div>
            <div>
              <label className="block text-xs font-black text-slate-600 uppercase tracking-wider mb-1.5">
                Address / Landmark (pincode required) <span className="text-rose-400">*</span>
              </label>
              <input type="text" name="landmark" value={formData.landmark} onChange={handleChange}
                className={inputCls(true, formData.landmark)} placeholder="Full address with pincode" />
            </div>
          </div>

          {/* Reporters & Pricing */}
          <div className="px-4 py-3 bg-gradient-to-r from-amber-600 to-orange-700 flex items-center gap-2">
            <FaUsers className="text-white/70 w-3.5 h-3.5" />
            <span className="text-white text-xs font-black uppercase tracking-widest">Reporters & Pricing</span>
          </div>
          <div className="p-4 sm:p-6 space-y-4">
            <div>
              <label className="block text-xs font-black text-slate-600 uppercase tracking-wider mb-1.5 flex items-center justify-between">
                <span>Number of Reporters <span className="text-rose-400">*</span></span>
                <span className="text-[9px] text-slate-400 font-bold">Max 50</span>
              </label>
              <div className="flex items-center gap-3">
                <input type="text" name="numberOfReporters" value={formData.numberOfReporters} onChange={handleChange}
                  className={`${inputCls(true, formData.numberOfReporters)} max-w-[120px]`}
                  placeholder="e.g. 3" inputMode="numeric" pattern="[0-9]*" />
                {isCalculatingPrice && (
                  <div className="flex items-center gap-2 text-amber-600 text-xs font-bold">
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Calculating...
                  </div>
                )}
              </div>
              <p className="text-[10px] text-slate-400 font-medium mt-1">Pricing is calculated automatically after you enter a value.</p>
            </div>

            {/* Live Pricing Card */}
            {showPricing && pricingData && formData.numberOfReporters >= 1 && (
              <div className="bg-gradient-to-br from-amber-500 to-yellow-600 rounded-2xl p-4 text-white shadow-lg shadow-amber-100">
                <p className="text-[10px] font-black uppercase tracking-widest text-white/70 mb-3">Price Breakdown</p>
                <div className="space-y-2">
                  {[
                    { label: "Reporters", val: pricingData.numberOfReporters },
                    { label: "Price / Reporter", val: `₹${pricingData.reporterPrice}` },
                    { label: "Subtotal", val: `₹${pricingData.subtotal}` },
                    { label: `GST (${pricingData.gstRate}%)`, val: `₹${pricingData.gstAmount}` },
                  ].map((r, i) => (
                    <div key={i} className="flex justify-between text-xs font-semibold">
                      <span className="text-white/70">{r.label}</span>
                      <span>{r.val}</span>
                    </div>
                  ))}
                  <div className="border-t border-white/20 pt-2 flex justify-between items-center">
                    <span className="text-sm font-black">Total Amount</span>
                    <span className="text-xl font-black">₹{pricingData.totalAmount}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Admin Note */}
          <div className="px-4 py-3 bg-gradient-to-r from-violet-600 to-purple-700 flex items-center gap-2">
            <FaStickyNote className="text-white/70 w-3.5 h-3.5" />
            <span className="text-white text-xs font-black uppercase tracking-widest">Note for Admin</span>
            <span className="ml-auto text-white/50 text-[9px] font-bold uppercase">Optional</span>
          </div>
          <div className="p-4 sm:p-6">
            <textarea name="adminNote" value={formData.adminNote} onChange={handleChange} rows={3}
              className={`${inputCls(false, formData.adminNote)} resize-none`}
              placeholder="Any special requirements for the admin..." />
          </div>

          {/* Terms + Submit */}
          <div className="px-4 sm:px-6 pb-6 space-y-4">
            <label className="flex items-start gap-3 p-3.5 bg-slate-50 rounded-2xl cursor-pointer border border-transparent hover:border-slate-200 transition-all">
              <input type="checkbox" id="termsAccepted" name="termsAccepted"
                checked={formData.termsAccepted} onChange={handleChange}
                className="mt-0.5 w-4 h-4 text-amber-500 border-slate-300 rounded focus:ring-amber-400 flex-shrink-0" />
              <span className="text-xs text-slate-600 leading-relaxed font-medium">
                <span className="text-slate-900 font-black">I confirm: </span>
                I have read all details and agree to the terms and conditions of the Paid Press Conference.
              </span>
            </label>

            <button type="submit" disabled={isSubmitting || isProcessingPayment}
              className={`w-full flex items-center justify-center gap-3 py-3.5 rounded-2xl text-sm font-black text-white shadow-lg transition-all active:scale-[0.98] ${isSubmitting || isProcessingPayment
                  ? "bg-slate-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-amber-500 to-yellow-600 shadow-amber-200 hover:shadow-amber-300 hover:shadow-xl"
                }`}>
              {isSubmitting || isProcessingPayment ? (
                <><svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>{isProcessingPayment ? "Processing Payment..." : "Submitting..."}</>
              ) : (
                <><FaCreditCard className="w-4 h-4" />
                  {pricingData && pricingData.totalAmount > 0 ? `Pay ₹${pricingData.totalAmount} & Submit` : "Submit Paid Conference"}
                  <FaArrowRight className="w-3 h-3" /></>
              )}
            </button>

            <p className="text-center text-[10px] text-slate-400 font-medium flex items-center justify-center gap-1.5">
              <FaShieldAlt className="w-3 h-3 text-amber-400" />
              Secure payment · Confirmed within 24 hours
            </p>
          </div>
        </form>
      </div>

      {/* ── Payment Options Bottom Sheet ── */}
      {showPaymentOptions && pricingData && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setShowPaymentOptions(false)} />
          <div className="relative bg-white rounded-t-[2rem] sm:rounded-[2rem] p-5 sm:p-8 w-full sm:max-w-md shadow-2xl z-10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-black text-slate-800">Choose Payment</h3>
              <button onClick={() => setShowPaymentOptions(false)} className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200">
                <FaTimes className="w-3 h-3" />
              </button>
            </div>

            {/* Summary pill */}
            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-3 mb-4 flex justify-between items-center">
              <div>
                <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider">{pricingData.numberOfReporters} Reporter(s) · GST {pricingData.gstRate}%</p>
                <p className="text-lg font-black text-amber-700">₹{pricingData.totalAmount}</p>
              </div>
              <FaDollarSign className="text-amber-300 w-8 h-8" />
            </div>

            <div className="space-y-2.5">
              {/* Wallet */}
              <button onClick={handleWalletPaymentClick}
                disabled={isProcessingPayment || walletBalance < pricingData.totalAmount}
                className={`w-full p-4 rounded-2xl border-2 flex items-center justify-between transition-all active:scale-[0.98] ${walletBalance >= pricingData.totalAmount
                    ? "border-emerald-400 bg-emerald-50 hover:bg-emerald-100"
                    : "border-slate-200 bg-slate-50 opacity-50 cursor-not-allowed"
                  }`}>
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${walletBalance >= pricingData.totalAmount ? "bg-emerald-100" : "bg-slate-100"}`}>
                    <FaWallet className={`w-4 h-4 ${walletBalance >= pricingData.totalAmount ? "text-emerald-600" : "text-slate-400"}`} />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-black text-slate-800">Pay with Wallet</p>
                    <p className="text-[10px] text-slate-500 font-medium">Balance: ₹{walletBalance.toFixed(2)}</p>
                  </div>
                </div>
                {walletBalance < pricingData.totalAmount && (
                  <span className="text-[10px] font-black text-rose-500 bg-rose-50 px-2 py-0.5 rounded-full">Low Balance</span>
                )}
              </button>

              {/* Razorpay */}
              <button onClick={handleRazorpayPaymentClick} disabled={isProcessingPayment}
                className="w-full p-4 rounded-2xl border-2 border-blue-400 bg-blue-50 hover:bg-blue-100 flex items-center justify-between transition-all active:scale-[0.98]">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center">
                    <FaCreditCard className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-black text-slate-800">Pay with Razorpay</p>
                    <p className="text-[10px] text-slate-500 font-medium">Card · UPI · Net Banking</p>
                  </div>
                </div>
                <FaChevronRight className="text-blue-400 w-3 h-3" />
              </button>
            </div>

            {isProcessingPayment && (
              <div className="flex items-center justify-center gap-2 mt-4 text-amber-600 text-xs font-bold">
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg> Processing payment...
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Payment Confirmation Bottom Sheet ── */}
      {showPaymentConfirmation && pricingData && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" />
          <div className="relative bg-white rounded-t-[2rem] sm:rounded-[2rem] p-5 sm:p-8 w-full sm:max-w-md shadow-2xl z-10 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-black text-slate-800">Confirm Payment</h3>
              <button onClick={() => { setShowPaymentConfirmation(false); setPaymentConfirmationChecked(false); }}
                className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                <FaTimes className="w-3 h-3" />
              </button>
            </div>

            <div className="bg-slate-50 rounded-2xl p-3 space-y-2">
              {[
                { label: "Method", val: pendingPaymentMethod === "wallet" ? "Wallet" : "Razorpay" },
                { label: "Reporters", val: pricingData.numberOfReporters },
                { label: "Subtotal", val: `₹${pricingData.subtotal}` },
                { label: `GST (${pricingData.gstRate}%)`, val: `₹${pricingData.gstAmount}` },
              ].map((r, i) => (
                <div key={i} className="flex justify-between text-xs">
                  <span className="text-slate-400 font-medium">{r.label}</span>
                  <span className="text-slate-700 font-black">{r.val}</span>
                </div>
              ))}
              <div className="border-t border-slate-200 pt-2 flex justify-between items-center">
                <span className="text-sm font-black text-slate-800">Total</span>
                <span className="text-base font-black text-amber-600">₹{pricingData.totalAmount}</span>
              </div>
            </div>

            <label className="flex items-start gap-3 p-3 bg-amber-50 rounded-2xl cursor-pointer border border-amber-100">
              <input type="checkbox" id="paymentConfirmation" checked={paymentConfirmationChecked}
                onChange={(e) => setPaymentConfirmationChecked(e.target.checked)}
                className="mt-0.5 w-4 h-4 text-amber-500 border-amber-300 rounded focus:ring-amber-400 flex-shrink-0" />
              <span className="text-xs text-slate-600 font-medium leading-relaxed">
                I understand and confirm this payment of <strong className="text-amber-700">₹{pricingData.totalAmount}</strong>.
              </span>
            </label>

            <div className="flex gap-2">
              <button onClick={() => { setShowPaymentConfirmation(false); setPaymentConfirmationChecked(false); }}
                className="flex-1 py-3 bg-slate-100 text-slate-700 rounded-2xl text-xs font-black active:scale-95 transition-all">
                Cancel
              </button>
              <button onClick={handlePaymentConfirmation} disabled={!paymentConfirmationChecked}
                className={`flex-1 py-3 rounded-2xl text-xs font-black text-white active:scale-95 transition-all ${paymentConfirmationChecked ? "bg-gradient-to-r from-amber-500 to-yellow-600 shadow-lg shadow-amber-100" : "bg-slate-300 cursor-not-allowed"
                  }`}>
                Yes, Pay Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaidPressConference;
